export {EventSubmissions as Page};

import {Helmet} from "react-helmet-async";
import {useContext, useEffect, useState} from "preact/hooks";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import messages from "../../../messages/messages.js";
import API_URL from "../../../includes/api.js";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import {useAuth} from "../../../components/AuthProvider.jsx";
import {formatServerDate, parseServerDate} from "../../../components/EventCountdown.jsx";
import {stageLabel} from "../../../includes/event.js";
import {EventEntryCard} from "../../../components/EventEntryCard.jsx";

function EventSubmissions() {
    const {addNotification} = useContext(NotificationContext);
    const {getToken, user, authLoading} = useAuth();

    const [submissions, setSubmissions] = useState([]);
    const [signups, setSignups] = useState([]);
    const [skew, setSkew] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => {
            fetch(`${API_URL}/event/my_submissions?firebase_auth_token=${token}`)
                .then(response => response.json()).then(data => {
                    if (data.code === 200) {
                        setSubmissions(data.data.submissions || []);
                        setSignups(data.data.signups || []);
                        const serverDate = parseServerDate(data.data.server_time);
                        if (serverDate) setSkew(serverDate.getTime() - Date.now());
                    } else {
                        addNotification(data.message, 'error');
                    }
                }).catch(() => {
                    addNotification('Failed to load your event submissions.', 'error');
                }).finally(() => {
                    setLoading(false);
                });
        });
    }, [authLoading, user]);

    return (<section className="text-white">

        <Helmet>
            <title>{messages["SITE.NAME"]} | My event submissions</title>
            <meta name="description" content="My event submissions"/>
        </Helmet>

        <div>

            <div className="flex flex-wrap">

                <AccountSidebar/>

                <div className="w-full md:w-3/4 px-0 md:px-4">

                    <div className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-3">

                        <h2 className="text-2xl font-bold mb-4">Event submissions</h2>
                        <p className="text-sm text-gray-400">
                            Every entry you have submitted to a community event, across all events.
                        </p>

                        {loading ? (
                            <div className="text-gray-400">Loading your submissions...</div>
                        ) : submissions.length === 0 ? (
                            <div className="text-gray-400">You have not submitted an entry to any event yet.</div>
                        ) : (
                            <div className="flex flex-col gap-6">
                                {submissions.map(submission => (
                                    <div key={submission.id}>
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className="font-bold">{submission.event.title}</span>
                                            {/* "contest" etc. appears only as an event-type label. */}
                                            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 text-gray-300 rounded-full px-2 py-0.5">
                                                {submission.event.event_type}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 text-gray-300 rounded-full px-2 py-0.5">
                                                {stageLabel(submission.event) || submission.event.stage}
                                            </span>
                                        </div>
                                        {/* Same entry card as the event's manage page, with the
                                            entry embedded for playback in place. */}
                                        <EventEntryCard submission={submission} skew={skew}>
                                            <div className="flex gap-4 mt-3 text-sm">
                                                <a href={`/event?e=${submission.event_id}`} className="text-blue-400 hover:underline">
                                                    View event
                                                </a>
                                                <a href={`/event/manage?e=${submission.event_id}`} className="text-blue-400 hover:underline">
                                                    Manage entry
                                                </a>
                                            </div>
                                        </EventEntryCard>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!loading && signups.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-lg font-bold mb-3">Signed up — no entry yet</h3>
                                <div className="flex flex-col gap-4">
                                    {signups.map(signup => (
                                        <div key={signup.event.id} className="border border-white/10 rounded-lg p-4">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-bold">{signup.event.title}</span>
                                                <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 text-gray-300 rounded-full px-2 py-0.5">
                                                    {signup.event.event_type}
                                                </span>
                                                <span className="text-[10px] uppercase tracking-widest font-semibold bg-white/10 text-gray-300 rounded-full px-2 py-0.5">
                                                    {stageLabel(signup.event) || signup.event.stage}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-1">
                                                Signed up {formatServerDate(signup.date_added, skew)}
                                            </div>
                                            {signup.submission_open && (
                                                <div className="text-xs text-green-400 mt-2">
                                                    Submissions are open — you can enter now.
                                                </div>
                                            )}
                                            <div className="flex gap-4 mt-3 text-sm">
                                                <a href={`/event?e=${signup.event.id}`} className="text-blue-400 hover:underline">
                                                    View event
                                                </a>
                                                {!signup.event.ended && (
                                                    <a href={`/event/manage?e=${signup.event.id}`} className="text-blue-400 hover:underline">
                                                        Submit an entry
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </section>);
}
